<script lang="ts">
  import { Ingresos, Camas, Pacientes, type Ingreso, type Cama } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';

  let items = $state<Ingreso[]>([]);
  let camas = $state<Cama[]>([]);
  let nombresPac = $state<Record<string, string>>({});
  let etiquetasCama = $state<Record<string, string>>({});
  let loading = $state(false);
  let error = $state<string | null>(null);

  let nuevo = $state<Ingreso>({ pacienteId: '', camaId: '', motivoIngreso: '' });

  async function cargar() {
    loading = true; error = null;
    try {
      const [ing, cam, pacs] = await Promise.all([Ingresos.activos(), Camas.list(), Pacientes.list().catch(() => [])]);
      items = ing; camas = cam;
      const np: Record<string, string> = {};
      for (const p of pacs) if (p.id) np[p.id] = `${p.nombres} ${p.apellidos}`.trim();
      nombresPac = np;
      const nc: Record<string, string> = {};
      for (const c of cam) if (c.id) nc[c.id] = [c.numero, c.servicio].filter(Boolean).join(' · ');
      etiquetasCama = nc;
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function crear(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevo.pacienteId) { error = 'Selecciona un paciente.'; return; }
    try {
      const body = { ...nuevo };
      await Ingresos.crear(body);
      nuevo = { pacienteId: '', camaId: '', motivoIngreso: '' };
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function darAlta(i: Ingreso) {
    if (!confirm(`¿Dar de alta a ${nombresPac[i.pacienteId] || 'este paciente'}?`)) return;
    try { await Ingresos.alta(i.id!); await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  $effect(() => { cargar(); });

  const camasDisponibles = $derived(camas.filter(c => c.estado === 'DISPONIBLE'));
</script>

<section>
  <header>
    <h2>Ingresos activos</h2>
    <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
  </header>

  {#if error}<p class="err">⚠ {error}</p>{/if}

  <form onsubmit={crear} class="row">
    <PacientePicker bind:value={nuevo.pacienteId} onPick={(id) => nuevo.pacienteId = id} />
    <select bind:value={nuevo.camaId} required>
      <option value="">— Cama —</option>
      {#each camasDisponibles as c}
        <option value={c.id}>{c.numero} {c.servicio ? '· ' + c.servicio : ''}</option>
      {/each}
    </select>
    <input placeholder="Motivo de ingreso" bind:value={nuevo.motivoIngreso} />
    <button type="submit" disabled={!nuevo.camaId || !nuevo.pacienteId}>Ingresar</button>
  </form>

  <table>
    <thead><tr><th>Paciente</th><th>Cama</th><th>Motivo</th><th>Desde</th><th></th></tr></thead>
    <tbody>
      {#each items as i (i.id)}
        <tr>
          <td>{nombresPac[i.pacienteId] || '—'}</td>
          <td>{etiquetasCama[i.camaId] || '—'}</td>
          <td>{i.motivoIngreso ?? '—'}</td>
          <td>{i.ingresadoEn ? new Date(i.ingresadoEn).toLocaleString() : ''}</td>
          <td><button onclick={() => darAlta(i)}>Dar alta</button></td>
        </tr>
      {:else}
        <tr><td colspan="5" class="empty">Sin ingresos activos.</td></tr>
      {/each}
    </tbody>
  </table>
</section>

<style>
  header { display: flex; justify-content: space-between; align-items: center; }
  .row { display: flex; gap: .5rem; flex-wrap: wrap; margin: 1rem 0; }
  .row input, .row select, .row button { padding: .45rem .6rem; border-radius: 8px; border: 1px solid #ccc; font: inherit; }
  .row input { flex: 1; min-width: 200px; }
  .row button { background: #0d6efd; color: #fff; border: 0; cursor: pointer; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: .55rem .6rem; border-bottom: 1px solid #eee; text-align: left; font-size: .92rem; }
  td button { padding: .25rem .55rem; border-radius: 6px; border: 1px solid #b00020; background: #fff; color: #b00020; cursor: pointer; font-size: .8rem; }
  .err { color: #b00020; }
  .empty { color: #888; text-align: center; font-style: italic; }
  code { font-size: .8rem; background: #f4f4f4; padding: .1rem .35rem; border-radius: 4px; }
</style>
